import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge, Card } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'

import type {
  AttendeeInput,
  PaymentChoice,
  PublicTicketType,
  Quote,
  SinglePreference,
} from './types'

/** Sélecteur de quantité — cibles ≥ 44 px, utilisable au pouce. */
export function QuantityStepper({
  value,
  onChange,
  max,
  disabled = false,
  label,
}: {
  value: number
  onChange: (value: number) => void
  max: number
  disabled?: boolean
  label: string
}) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        aria-label={`− ${label}`}
        disabled={disabled || value <= 0}
        onClick={() => onChange(Math.max(0, value - 1))}
        className="h-11 w-11 rounded-full border border-line text-content-primary disabled:opacity-30"
      >
        −
      </button>
      <span
        aria-live="polite"
        className="w-8 text-center font-display text-heading-md tabular-nums text-content-primary"
      >
        {value}
      </span>
      <button
        type="button"
        aria-label={`+ ${label}`}
        disabled={disabled || value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
        className="h-11 w-11 rounded-full border border-line text-content-primary disabled:opacity-30"
      >
        +
      </button>
    </div>
  )
}

export function TicketTypeCard({
  ticketType,
  quantity,
  onChange,
  locale,
}: {
  ticketType: PublicTicketType
  quantity: number
  onChange: (value: number) => void
  locale: string
}) {
  const { t } = useTranslation()

  const soldOut = ticketType.availability === 'sold_out'
  const selectable = ticketType.on_sale && !soldOut
  const max = ticketType.max_per_order ?? 10

  return (
    <Card className={cn('flex flex-col gap-4', !selectable && 'opacity-60')}>
      <div className="flex items-start justify-between gap-6">
        <div>
          <h3 className="font-display text-heading-md text-content-primary">
            {ticketType.name}
          </h3>
          <p className="mt-1 text-sm text-content-secondary">
            {ticketType.admits_count > 1
              ? t('ticketing.perPeople', { count: ticketType.admits_count })
              : t('ticketing.perPerson')}
          </p>
        </div>
        <p className="shrink-0 font-display text-heading-md tabular-nums text-champagne-300">
          {formatMoney(
            { amount_minor: ticketType.price_minor, currency: ticketType.currency },
            locale,
          )}
        </p>
      </div>

      {ticketType.description ? (
        <p className="text-sm text-content-secondary">{ticketType.description}</p>
      ) : null}

      {ticketType.benefits.length > 0 ? (
        <ul className="space-y-1 text-sm text-content-secondary">
          {ticketType.benefits.map((benefit) => (
            <li key={benefit}>· {benefit}</li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {soldOut ? <Badge tone="neutral">{t('ticketing.soldOut')}</Badge> : null}
          {!soldOut && ticketType.availability === 'low' ? (
            <Badge tone="ember">{t('ticketing.low')}</Badge>
          ) : null}
          {ticketType.remaining !== null ? (
            <Badge tone="neutral">
              {t('ticketing.remaining', { count: ticketType.remaining })}
            </Badge>
          ) : null}
          {!ticketType.on_sale && ticketType.sale_start ? (
            <Badge tone="champagne">
              {t('ticketing.notYetOnSale', {
                date: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
                  new Date(ticketType.sale_start),
                ),
              })}
            </Badge>
          ) : null}
        </div>

        {selectable ? (
          <QuantityStepper
            value={quantity}
            onChange={onChange}
            max={max}
            label={ticketType.name}
          />
        ) : null}
      </div>

      {ticketType.max_per_order ? (
        <p className="text-xs text-content-muted">
          {t('ticketing.maxPerOrder', { count: ticketType.max_per_order })}
        </p>
      ) : null}
    </Card>
  )
}

/** Récapitulatif : reflet exact du devis serveur, jamais un calcul local. */
export function QuoteSummary({
  quote,
  locale,
  children,
}: {
  quote: Quote | null
  locale: string
  children?: ReactNode
}) {
  const { t } = useTranslation()

  return (
    <Card className="sticky top-24">
      <p className="overline text-content-muted">{t('ticketing.summary')}</p>

      {quote && quote.lines.length > 0 ? (
        <>
          <ul className="mt-4 space-y-4">
            {quote.lines.map((line) => (
              <li key={`${line.type}:${line.ref_id}`} className="text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-content-secondary">
                    {line.quantity} × {line.label}
                    {line.party_size ? ` · ${line.party_size}p` : ''}
                  </span>
                  <span className="tabular-nums text-content-primary">
                    {formatMoney(
                      { amount_minor: line.line_total_minor, currency: quote.currency },
                      locale,
                    )}
                  </span>
                </div>

                {/* Détail d'une table : pourquoi telle bouteille est à zéro. */}
                {line.table_components && line.table_components.length > 0 ? (
                  <ul className="mt-2 space-y-1 border-l border-line-subtle pl-3">
                    {line.table_components.map((component) => (
                      <li
                        key={component.drink_item_id}
                        className="flex justify-between gap-4 text-xs text-content-muted"
                      >
                        <span>
                          {component.quantity} × {component.label}
                          {component.included > 0
                            ? ` (${component.included} ${t('tables.componentIncluded')})`
                            : ''}
                        </span>
                        <span className="tabular-nums">
                          {formatMoney(
                            {
                              amount_minor: component.line_total_minor,
                              currency: quote.currency,
                            },
                            locale,
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>

          {/* Sous-total et ajustements : le client voit d'où vient son prix. */}
          {quote.adjustments.length > 0 ? (
            <div className="mt-5 space-y-2 border-t border-line-subtle pt-5 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-content-muted">{t('promo.subtotal')}</span>
                <span className="tabular-nums text-content-secondary">
                  {formatMoney(
                    { amount_minor: quote.subtotal_minor, currency: quote.currency },
                    locale,
                  )}
                </span>
              </div>
              {quote.adjustments.map((adjustment, index) => (
                <div
                  key={`${adjustment.kind}-${index}`}
                  className="flex justify-between gap-4"
                >
                  <span
                    className={
                      adjustment.amount_minor < 0 ? 'text-state-success' : 'text-content-muted'
                    }
                  >
                    {adjustment.label}
                  </span>
                  <span
                    className={cn(
                      'tabular-nums',
                      adjustment.amount_minor < 0
                        ? 'text-state-success'
                        : 'text-content-secondary',
                    )}
                  >
                    {adjustment.amount_minor > 0 ? '+' : ''}
                    {formatMoney(
                      { amount_minor: adjustment.amount_minor, currency: quote.currency },
                      locale,
                    )}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          <div className="mt-5 flex items-baseline justify-between border-t border-line-subtle pt-5">
            <span className="overline text-content-muted">{t('ticketing.total')}</span>
            <span className="font-display text-heading-lg tabular-nums text-content-primary">
              {formatMoney(
                { amount_minor: quote.total_minor, currency: quote.currency },
                locale,
              )}
            </span>
          </div>

          {quote.warnings.map((warning) => (
            <p
              key={warning.code}
              className="mt-4 rounded-md border border-line-subtle bg-bg-overlay p-3 text-xs text-content-secondary"
            >
              ⚠️ {warning.message}
            </p>
          ))}
        </>
      ) : (
        <p className="mt-4 text-sm text-content-muted">{t('ticketing.emptyBasket')}</p>
      )}

      {children ? <div className="mt-6">{children}</div> : null}
    </Card>
  )
}

// ── Formulaire ─────────────────────────────────────────────────────

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <span className="overline text-content-muted">{label}</span>
      {hint ? <span className="ml-2 text-xs text-content-muted">({hint})</span> : null}
      <div className="mt-2">{children}</div>
    </label>
  )
}

const INPUT_CLASS =
  'h-12 w-full rounded-md border border-line bg-bg-inset px-4 text-content-primary ' +
  'placeholder:text-content-muted focus:border-ember-400'

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cn(INPUT_CLASS, props.className)} />
}

export function Checkbox({
  checked,
  onChange,
  children,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  children: ReactNode
}) {
  return (
    <label className="flex items-start gap-3 py-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-5 w-5 shrink-0 accent-[var(--ember-500)]"
      />
      <span className="text-sm text-content-secondary">{children}</span>
    </label>
  )
}

/**
 * Question du bracelet célibataire.
 *
 * RULE 7 : la réponse est **privée**. Elle n'apparaît nulle part publiquement
 * et ne sert qu'à remettre le bracelet à l'entrée. « Je préfère ne pas
 * répondre » est une réponse à part entière, pas un refus déguisé.
 */
export function SinglePreferenceField({
  value,
  onChange,
}: {
  value: SinglePreference | undefined
  onChange: (value: SinglePreference) => void
}) {
  const { t } = useTranslation()

  const options: { value: SinglePreference; label: string }[] = [
    { value: 'yes', label: t('ticketing.singleYes') },
    { value: 'no', label: t('ticketing.singleNo') },
    { value: 'prefer_not_to_say', label: t('ticketing.singleUnsure') },
  ]

  return (
    <fieldset className="mt-6 rounded-md border border-line-subtle p-4">
      <legend className="px-2 text-sm text-content-primary">
        💙 {t('ticketing.singleQuestion')}
      </legend>
      <p className="mb-3 text-xs text-content-muted">{t('ticketing.singleHelp')}</p>

      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-full border px-4 py-2 text-sm transition',
              value === option.value
                ? 'border-ember-400 text-content-primary'
                : 'border-line text-content-secondary hover:border-line-strong',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  )
}

/**
 * L'accompagnant : **son nom, rien d'autre**.
 *
 * Pas d'adresse, pas de téléphone. Il n'y a qu'un QR, il part chez l'acheteur,
 * et ces personnes ne reçoivent aucun message — leur demander une adresse
 * serait collecter une donnée dont rien ne se sert, et faire trébucher
 * quelqu'un qui, à cet instant, ne connaît pas par cœur l'e-mail de son
 * conjoint. Le nom suffit : il sert à l'appeler à l'entrée.
 */
export function AttendeeFields({
  index,
  attendee,
  onChange,
}: {
  index: number
  attendee: AttendeeInput
  onChange: (attendee: AttendeeInput) => void
}) {
  const { t } = useTranslation()

  return (
    <Card className="space-y-4">
      <p className="overline text-champagne-400">
        {t('ticketing.guestName', { index: index + 1 })}
      </p>

      {/* Le prénom seulement : c'est ce que lit le contrôle à l'entrée, et
          l'acheteur n'a pas à connaître l'état civil de ses amis. */}
      <Field label={t('ticketing.firstName')}>
        <TextInput
          required
          autoComplete="off"
          value={attendee.first_name}
          onChange={(event) => onChange({ ...attendee, first_name: event.target.value })}
        />
      </Field>

      <SinglePreferenceField
        value={attendee.single_preference}
        onChange={(single_preference) => onChange({ ...attendee, single_preference })}
      />
    </Card>
  )
}

/**
 * Code promo.
 *
 * La validité est décidée par le serveur : un code inconnu ne bloque jamais
 * l'achat, il produit simplement un avertissement dans le devis.
 */
export function PromoCodeField({
  value,
  onApply,
}: {
  value: string
  onApply: (code: string) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(value)

  return (
    <div className="mb-6 mt-6 border-t border-line-subtle pt-5">
      <label className="overline text-content-muted" htmlFor="promo-code">
        {t('promo.label')}
      </label>
      <div className="mt-2 flex gap-2">
        <input
          id="promo-code"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('promo.placeholder')}
          className={cn(INPUT_CLASS, 'h-11 uppercase')}
        />
        <button
          type="button"
          onClick={() => onApply(draft.trim())}
          className="h-11 shrink-0 rounded-md border border-line px-4 text-sm text-content-primary"
        >
          {t('promo.apply')}
        </button>
      </div>
    </div>
  )
}

/**
 * Comment le client compte régler.
 *
 * Ce n'est pas un paiement : c'est une **annonce**, faite avant que quoi que
 * ce soit ne soit encaissé. Elle décide du message qu'il recevra — lien et
 * référence, ou consigne de venir payer à l'entrée — et permet à l'équipe, la
 * veille de la soirée, de distinguer une commande qu'on attend d'un panier
 * abandonné.
 *
 * Les options ouvertes viennent des réglages : l'équipe ferme le règlement à
 * l'entrée sans qu'on touche au code, et une option fermée ne s'affiche pas.
 */
export function PaymentChoiceField({
  value,
  onChange,
  available,
}: {
  value: PaymentChoice | null
  onChange: (value: PaymentChoice) => void
  available: PaymentChoice[]
}) {
  const { t } = useTranslation()
  if (available.length === 0) return null

  return (
    <fieldset className="border-t border-line-subtle pt-4">
      <legend className="overline text-content-muted">{t('ticketing.paymentChoice')}</legend>
      <p className="mt-1 text-sm text-content-secondary">{t('ticketing.paymentChoiceHint')}</p>
      <div className="mt-3 space-y-2">
        {available.map((option) => (
          <label
            key={option}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition',
              value === option
                ? 'border-ember-500 bg-ember-500/10'
                : 'border-line-subtle hover:border-line-strong',
            )}
          >
            <input
              type="radio"
              name="payment-choice"
              checked={value === option}
              onChange={() => onChange(option)}
              className="mt-1 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
            />
            <span>
              <span className="block text-sm text-content-primary">
                {t(`ticketing.paymentChoiceOption.${option}`)}
              </span>
              <span className="mt-0.5 block text-xs text-content-muted">
                {t(`ticketing.paymentChoiceDetail.${option}`)}
              </span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}
